import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, Optional, Output, OnInit } from "@angular/core";
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { DeviceModel } from "app/models/device.model";
import { NotificationChannelModel } from "app/models/notificationChannel.model";
import { ProjectModel, ProjectPeriodModel } from "app/models/project.model";
import {
    LF010DevicePeriod,
    ProjectDeviceModel,
    ProjectDeviceSettings,
    ScheduledRestart,
    ScheduledRestartFrequency,
    XL3ConnectionMode,
} from "app/models/projectDevice.model";
import { DeviceService } from "app/services/api/device.service";
import { ManufacturerService } from "app/services/api/manufacturer.service";
import { GeocodingService } from "app/services/geocoding.service";
import { FileSystemFileEntry, NgxFileDropEntry } from "ngx-file-drop";
import { ToastrService } from "ngx-toastr";
import { locale as catalan } from "../../../i18n/ca";
import { locale as english } from "../../../i18n/en";
import { locale as spanish } from "../../../i18n/es";
import { UploadUtils } from "app/shared/upload.utils";
import { Cloudinary } from "@cloudinary/url-gen";
import { CloudinaryImage } from "@cloudinary/url-gen/assets/CloudinaryImage";
import { fill, limitFit } from "@cloudinary/url-gen/actions/resize";
import { Icon, LeafletMouseEvent, Map as LeafletMap, icon, latLng, marker, tileLayer } from "leaflet";
import { finalize } from "rxjs/operators";
import { forkJoin } from "rxjs";
import {
    buildMissingSvantekDefaultTokens,
    buildSvantekDiffTokenSegments,
    DEFAULT_SVANTEK_CUSTOM_CONFIG,
    getDefaultSvantekVisualConfig,
    parseSvantekCustomConfig,
    ParsedSvantekCustomConfig,
    serializeSvantekCustomConfig,
    SvantekDiffTokenSegment,
    SvantekVisualConfigState,
} from "./svantek-custom-config";

enum ProjectAction {
    NEW = "NEW",
    EDIT = "EDIT",
}

enum SvantekRestartWeekday {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

@Component({
    selector: "app-project-device-add-edit",
    templateUrl: "./project-device-add-edit.component.html",
    styleUrls: ["./project-device-add-edit.component.scss"],
    standalone: false,
})
export class ProjectDeviceAddEditComponent implements OnInit {
    private readonly defaultLF010DevicePeriods: LF010DevicePeriod[] = [
        { devicePeriod: 1, name: "Day" },
        { devicePeriod: 2, name: "Evening" },
        { devicePeriod: 3, name: "Night" },
    ];
    private readonly cloudinary = new Cloudinary({
        cloud: { cloudName: "emmadb" },
    });
    private readonly cloudinaryImageCache = new Map<string, CloudinaryImage>();
    private readonly ntiProtocolName = "XL3 Streaming";
    private readonly bruelProtocolName = "webserver";
    private readonly svantekProtocolName = "direct";
    private readonly defaultSvantekConfigValue = DEFAULT_SVANTEK_CUSTOM_CONFIG;
    private readonly ntiManufacturerName = "NTI";
    private readonly defaultXL3StorageMB = 32768;
    private readonly bruelManufacturerName = "Bruel & Kjær";
    private readonly svantekManufacturerName = "Svantek";
    private readonly defaultMapZoom = 13;
    private readonly selectedMapZoom = 16;
    private readonly defaultMapCenter = latLng(41.3874, 2.1686);
    private readonly defaultScheduledRestart: ScheduledRestart = {
        enabled: false,
        frequency: ScheduledRestartFrequency.Weekly,
        weekday: SvantekRestartWeekday.Monday,
        hour: 6,
        minute: 45,
    };
    private readonly locationMarkerIcon: Icon = icon({
        iconUrl: "assets/blue-marker-noise-sensor.png",
        iconRetinaUrl: "assets/blue-marker-noise-sensor.png",
        iconSize: [50, 69],
        iconAnchor: [25, 69],
        popupAnchor: [0, -69],
        shadowUrl: undefined,
        className: "device-location-marker",
    });
    readonly xl3ConnectionType = XL3ConnectionMode;
    readonly scheduledRestartFrequency = ScheduledRestartFrequency;
    readonly svantekMicrophoneCompensationOptions = [
        { value: "", label: "DEVICES.SvantekUseInstrumentDefault" },
        { value: "1", label: "DEVICES.SvantekOn" },
        { value: "0", label: "DEVICES.SvantekOff" },
    ];
    readonly svantekFreeFieldCompensationOptions = [
        { value: "", label: "DEVICES.SvantekUseInstrumentDefault" },
        { value: "0", label: "DEVICES.SvantekOff" },
        { value: "1", label: "DEVICES.SvantekEnvironmental" },
        { value: "2", label: "DEVICES.SvantekAirport" },
    ];
    readonly svantekWaveRecordingModeOptions = [
        { value: "0", label: "DEVICES.SvantekWaveRecordingOff" },
        { value: "1", label: "DEVICES.SvantekWaveRecordingContinuous" },
        { value: "2", label: "DEVICES.SvantekWaveRecordingSlopePlus" },
        { value: "3", label: "DEVICES.SvantekWaveRecordingSlopeMinus" },
        { value: "6", label: "DEVICES.SvantekWaveRecordingGradientPlus" },
        { value: "7", label: "DEVICES.SvantekWaveRecordingManual" },
        { value: "8", label: "DEVICES.SvantekWaveRecordingIntegrationPeriod" },
        { value: "4", label: "DEVICES.SvantekWaveRecordingLevelPlus" },
        { value: "5", label: "DEVICES.SvantekWaveRecordingLevelMinus" },
        { value: "10", label: "DEVICES.SvantekWaveRecordingExternal" },
        { value: "11", label: "DEVICES.SvantekWaveRecordingAlarm" },
    ];
    readonly svantekWaveFormatOptions = [
        { value: "0", label: "DEVICES.SvantekFormatPcm" },
        { value: "1", label: "DEVICES.SvantekFormatExtensible" },
        { value: "2", label: "DEVICES.SvantekFormatALaw" },
    ];
    readonly svantekBitsPerSampleOptions = [
        { value: "16", label: "DEVICES.SvantekBits16" },
        { value: "24", label: "DEVICES.SvantekBits24" },
    ];
    readonly svantekWaveRecordingFilterOptions = [
        { value: "1", label: "DEVICES.SvantekFilterZ" },
        { value: "2", label: "DEVICES.SvantekFilterA" },
        { value: "3", label: "DEVICES.SvantekFilterC" },
        { value: "5", label: "DEVICES.SvantekFilterB" },
    ];
    readonly svantekWaveSamplingRateOptions = [
        { value: "0", label: "DEVICES.SvantekSampling48kHz" },
        { value: "1", label: "DEVICES.SvantekSampling24kHz" },
        { value: "2", label: "DEVICES.SvantekSampling12kHz" },
        { value: "3", label: "DEVICES.SvantekSampling6kHz" },
    ];
    readonly svantekLengthLimitOptions = [
        { value: "4gb", label: "DEVICES.SvantekLengthLimit4Gb" },
        { value: "minutes", label: "DEVICES.SvantekLengthLimitMinutes" },
    ];
    readonly svantekTriggerPeriodOptions = [
        { value: "0", label: "DEVICES.SvantekTriggerPeriodLoggerStep" },
        { value: "5", label: "DEVICES.SvantekTriggerPeriodHalfMillisecond" },
        { value: "1000", label: "DEVICES.SvantekTriggerPeriod100Milliseconds" },
        { value: "10000", label: "DEVICES.SvantekTriggerPeriod1Second" },
    ];
    readonly svantekBaselineSummary = [
        { label: "DEVICES.SvantekBaselineMeasurementMode", value: "DEVICES.SvantekBaselineMeasurementModeValue" },
        { label: "DEVICES.SvantekBaselineStartSynchronization", value: "DEVICES.SvantekBaselineStartSynchronizationValue" },
        { label: "DEVICES.SvantekBaselineIntegrationPeriod", value: "DEVICES.SvantekBaselineIntegrationPeriodValue" },
        { label: "DEVICES.SvantekBaselineRepetitionCycles", value: "DEVICES.SvantekBaselineRepetitionCyclesValue" },
        { label: "DEVICES.SvantekBaselineLogger", value: "DEVICES.SvantekBaselineLoggerValue" },
        { label: "DEVICES.SvantekBaselineProfiles", value: "DEVICES.SvantekBaselineProfilesValue" },
        { label: "DEVICES.SvantekBaselineSpectrumFilter", value: "DEVICES.SvantekBaselineSpectrumFilterValue" },
        { label: "DEVICES.SvantekBaselineLoggerStep", value: "DEVICES.SvantekBaselineLoggerStepValue" },
        { label: "DEVICES.SvantekBaselineLeqDetector", value: "DEVICES.SvantekBaselineLeqDetectorValue" },
    ];
    readonly svantekRestartWeekdays = [
        { value: SvantekRestartWeekday.Monday, label: "DEVICES.WeekdayMonday" },
        {
            value: SvantekRestartWeekday.Tuesday,
            label: "DEVICES.WeekdayTuesday",
        },
        {
            value: SvantekRestartWeekday.Wednesday,
            label: "DEVICES.WeekdayWednesday",
        },
        {
            value: SvantekRestartWeekday.Thursday,
            label: "DEVICES.WeekdayThursday",
        },
        { value: SvantekRestartWeekday.Friday, label: "DEVICES.WeekdayFriday" },
        {
            value: SvantekRestartWeekday.Saturday,
            label: "DEVICES.WeekdaySaturday",
        },
        { value: SvantekRestartWeekday.Sunday, label: "DEVICES.WeekdaySunday" },
    ];
    readonly mapOptions = {
        layers: [
            tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                maxZoom: 19,
                attribution: "&copy; OpenStreetMap contributors",
            }),
        ],
        zoom: this.defaultMapZoom,
    };

    /** When used inside a drawer (no dialog). */
    @Input() drawerDevice: ProjectDeviceModel | null = null;
    @Input() drawerProject: ProjectModel | null = null;
    @Input() drawerDevices: ProjectDeviceModel[] = [];
    @Input() drawerAction: "new" | "edit" = "new";
    @Output() closed = new EventEmitter<any>();

    public deviceForm: UntypedFormGroup;
    device: ProjectDeviceModel;
    deviceName: string;
    devices: DeviceModel[] = [];
    /** Map of manufacturer id to name (from API), used for NTI/Bruel/Svantek detection. */
    private manufacturerNameById: Record<string, string> = {};
    projectDevices: ProjectDeviceModel[];
    project: ProjectModel;
    action: ProjectAction;
    editImage: boolean;

    public uploadedFile: File = undefined;
    public imageUrl: any = undefined;
    public imageLoading = false;
    public addressLookupLoading = false;
    public reverseLookupLoading = false;
    public mapCenter = this.defaultMapCenter;
    public mapZoom = this.defaultMapZoom;
    public mapLayers = [];
    public deviceMetadataLoaded = false;
    public deviceMetadataLoading = false;

    private map: LeafletMap | null = null;
    private lastResolvedAddress = "";
    private svantekConfigSyncing = false;
    private svantekConfigState: ParsedSvantekCustomConfig = parseSvantekCustomConfig(null);

    constructor(
        private formBuilder: UntypedFormBuilder,
        private translation: FuseTranslationLoaderService,
        private deviceService: DeviceService,
        private manufacturerService: ManufacturerService,
        private geocodingService: GeocodingService,
        @Optional()
        public matDialogRef: MatDialogRef<ProjectDeviceAddEditComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) private _data: any,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);

        // When opened as dialog, set data from MAT_DIALOG_DATA
        if (_data?.action === "new") {
            this.action = ProjectAction.NEW;
            this.projectDevices = _data.devices;
            this.project = _data.project;
        } else if (_data?.action === "edit") {
            this.action = ProjectAction.EDIT;
            this.device = _data.device;
            this.project = _data.project;
        }
    }

    // Initialize the component state.
    ngOnInit() {
        // When used in drawer, @Input() are set by now
        if (!this._data) {
            if (this.drawerAction === "new") {
                this.action = ProjectAction.NEW;
                this.projectDevices = this.drawerDevices ?? [];
                this.project = this.drawerProject;
            } else if (this.drawerAction === "edit" && this.drawerDevice) {
                this.action = ProjectAction.EDIT;
                this.device = this.drawerDevice;
                this.project = this.drawerProject;
            }
        }
        this.createForm();
        this.bindLocationChanges();
        this.updateDeviceData();
        this.updateMapLocation();
        this.lastResolvedAddress = this.getCurrentAddress();
    }

    // Get the selected base device.
    get baseDevice() {
        if (!this.devices) {
            return undefined;
        }

        if (this.action === ProjectAction.EDIT) {
            return this.devices.find((obj) => obj.id === this.device.baseDevice);
        } else {
            return this.devices.find((obj) => obj.id === this.deviceForm.get("baseDevice").value);
        }
    }

    // Copy the base device name into the custom name field.
    public updatedBaseDevice() {
        if (!this.deviceForm.get("name").value) {
            this.deviceForm.get("name").setValue(this.baseDevice.name);
        }
    }

    // Save the device payload.
    public save() {
        if (this.deviceMetadataLoading || this.isReadOnly) {
            return;
        }

        if (!this.deviceMetadataLoaded) {
            this.updateDeviceData();
            return;
        }

        if (this.deviceForm.invalid) {
            return;
        }

        // Collect the form values.
        const saveDevice = this.deviceForm.getRawValue();
        delete saveDevice.xl3ConnectionType;
        delete saveDevice.svantekConfigBuilder;
        if (!this.isNTIDevice) {
            delete saveDevice.storage;
        }
        saveDevice.settings = this.normalizeSettings(saveDevice.settings, this.deviceForm.get("xl3ConnectionType").value);

        // Keep immutable fields untouched for active devices.
        if (this.device && this.device.active) {
            delete saveDevice.location;
            delete saveDevice.dataFrequency;
        }

        // Emit the dialog result.
        const result = {
            action: this.action === ProjectAction.NEW ? "new" : "edit",
            device: saveDevice,
            file: this.uploadedFile,
            editImage: this.editImage,
        };
        this.closed.emit(result);
        this.matDialogRef?.close(result);
    }

    // Close the editor without saving.
    public cancel() {
        this.closed.emit();
        this.matDialogRef?.close();
    }

    // Build the reactive form.
    private createForm() {
        // Resolve the initial XL3 connection type.
        const connectionSettings = this.device?.settings?.connection;
        const runtimeSettings = this.device?.settings?.runtime;
        const specialAlarmSettings = this.device?.settings?.specialAlarms;
        const scheduledRestart = {
            ...this.defaultScheduledRestart,
            ...(this.device?.settings?.scheduling?.restart ?? {}),
        };
        const initialConnectionType =
            connectionSettings?.mode ?? (connectionSettings?.connectKey ? XL3ConnectionMode.Connect : XL3ConnectionMode.Lan);

        // Create the form controls.
        this.deviceForm = this.formBuilder.group({
            id: [this.device ? this.device.id : null],
            name: [this.device ? this.device.name : null, [Validators.required]],
            baseDevice: [Validators.required],
            comments: [this.device ? this.device.comments : null],
            ftpFolder: [this.device ? this.device.ftpFolder : null],
            xl3ConnectionType: [initialConnectionType],
            dataFrequency: [this.device ? this.device.dataFrequency : null],
            storage: [this.device?.storage ?? null],
            location: this.formBuilder.group({
                address: [this.device ? this.device.location.address : undefined],
                latitude: [
                    this.device ? this.device.location.latitude : undefined,
                    [Validators.required, Validators.min(-90), Validators.max(+90)],
                ],
                longitude: [
                    this.device ? this.device.location.longitude : undefined,
                    [Validators.required, Validators.min(-180), Validators.max(+180)],
                ],
            }),
            settings: this.formBuilder.group({
                connection: this.formBuilder.group({
                    host: [connectionSettings?.host ?? null],
                    password: [connectionSettings?.password ?? null],
                    connectKey: [connectionSettings?.connectKey ?? null],
                }),
                lf010: this.formBuilder.group({
                    periodMappings: this.formBuilder.array([]),
                }),
                runtime: this.formBuilder.group({
                    customConfig: [runtimeSettings?.customConfig ?? null],
                    fileSyncEnabled: [runtimeSettings?.fileSyncEnabled === true],
                }),
                scheduling: this.formBuilder.group({
                    restart: this.formBuilder.group({
                        enabled: [scheduledRestart.enabled],
                        frequency: [scheduledRestart.frequency],
                        weekday: [scheduledRestart.weekday],
                        monthDay: [scheduledRestart.monthDay],
                        hour: [scheduledRestart.hour, [Validators.min(0), Validators.max(23)]],
                        minute: [scheduledRestart.minute, [Validators.min(0), Validators.max(59)]],
                    }),
                }),
                specialAlarms: this.formBuilder.group({
                    connectionEnabled: [!!specialAlarmSettings?.connectionEnabled],
                    powerSupplyEnabled: [!!specialAlarmSettings?.powerSupplyEnabled],
                    notificationChannelIds: [[...(specialAlarmSettings?.notificationChannelIds ?? [])]],
                }),
            }),
            svantekConfigBuilder: this.formBuilder.group({
                waveFileName: [getDefaultSvantekVisualConfig().waveFileName],
                microphoneCompensation: [getDefaultSvantekVisualConfig().microphoneCompensation],
                freeFieldCompensation: [getDefaultSvantekVisualConfig().freeFieldCompensation],
                waveRecordingMode: [getDefaultSvantekVisualConfig().waveRecordingMode, [Validators.required]],
                format: [getDefaultSvantekVisualConfig().format, [Validators.required]],
                bitsPerSample: [getDefaultSvantekVisualConfig().bitsPerSample, [Validators.required]],
                recordingTime: [getDefaultSvantekVisualConfig().recordingTime, [Validators.min(1), Validators.max(28800)]],
                preTrigger: [getDefaultSvantekVisualConfig().preTrigger, [Validators.min(0), Validators.max(60)]],
                recordingFilter: [getDefaultSvantekVisualConfig().recordingFilter, [Validators.required]],
                samplingRate: [getDefaultSvantekVisualConfig().samplingRate, [Validators.required]],
                gain: [getDefaultSvantekVisualConfig().gain, [Validators.min(0), Validators.max(40)]],
                triggerLevel: [getDefaultSvantekVisualConfig().triggerLevel, [Validators.min(24), Validators.max(136)]],
                triggerPeriod: [getDefaultSvantekVisualConfig().triggerPeriod, [Validators.required]],
                triggerGradient: [getDefaultSvantekVisualConfig().triggerGradient, [Validators.min(1), Validators.max(100)]],
                lengthLimitMode: [getDefaultSvantekVisualConfig().lengthLimitMode, [Validators.required]],
                lengthLimitMinutes: [getDefaultSvantekVisualConfig().lengthLimitMinutes, [Validators.min(1), Validators.max(480)]],
            }),
        });
        this.rebuildLF010PeriodMappings();

        if (this.isReadOnly) {
            this.deviceForm.disable({ emitEvent: false });
        }

        // Update dependent validators when the device changes.
        this.deviceForm.get("baseDevice").valueChanges.subscribe(() => {
            this.updatedBaseDevice();
            this.updateStorageControl();
            this.updateXL3Validators();
            this.applySvantekConfigState(parseSvantekCustomConfig(this.deviceForm.get("settings.runtime.customConfig").value));
        });

        // Update dependent validators when the XL3 mode changes.
        this.deviceForm.get("xl3ConnectionType").valueChanges.subscribe(() => {
            this.updateXL3Validators();
        });
        this.updateXL3Validators();
        this.deviceForm.get("settings.scheduling.restart.enabled").valueChanges.subscribe(() => {
            this.updateScheduledRestartValidators();
        });
        this.deviceForm.get("settings.scheduling.restart.frequency").valueChanges.subscribe(() => {
            this.updateScheduledRestartValidators();
        });
        this.updateScheduledRestartValidators();
        this.bindSvantekConfigChanges();
    }

    // Watch the location controls and refresh the map marker.
    private bindLocationChanges() {
        // Get the location controls.
        const latitudeControl = this.deviceForm.get("location.latitude");
        const longitudeControl = this.deviceForm.get("location.longitude");

        // Refresh the map when latitude changes.
        latitudeControl.valueChanges.subscribe(() => {
            this.updateMapLocation();
        });

        // Refresh the map when longitude changes.
        longitudeControl.valueChanges.subscribe(() => {
            this.updateMapLocation();
        });
    }

    // Load the base device catalog and initial image.
    private updateDeviceData() {
        // Restore the current image while editing.
        if (this.action === ProjectAction.EDIT && !this.editImage && !this.uploadedFile) {
            this.imageUrl = this.device.picture;
        }

        this.deviceMetadataLoaded = false;
        this.deviceMetadataLoading = true;

        // Load base devices and manufacturers (for NTI/Bruel/Svantek detection by name).
        forkJoin({
            devices: this.deviceService.getAll(),
            manufacturers: this.manufacturerService.getAll(),
        }).subscribe(
            ({ devices, manufacturers }) => {
                this.devices = devices;
                this.manufacturerNameById = manufacturers.reduce((acc, m) => ({ ...acc, [m.id]: m.name }), {} as Record<string, string>);

                // Restore the base device while editing.
                if (this.action === ProjectAction.EDIT) {
                    this.deviceForm.get("baseDevice").setValue(this.baseDevice.id);
                    this.deviceForm.get("baseDevice").disable();
                    this.updatedBaseDevice();
                }
                this.updateStorageControl();
                this.updateXL3Validators();
                this.deviceMetadataLoaded = true;
                this.deviceMetadataLoading = false;
                this.cdr.detectChanges();
            },
            (error) => {
                this.deviceMetadataLoading = false;
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.cdr.detectChanges();
            },
        );
    }

    // Remove the selected image.
    public removeImage() {
        if (this.isReadOnly) {
            return;
        }

        this.uploadedFile = undefined;
        this.imageUrl = undefined;

        this.editImage = true;
    }

    // Read the dropped image file.
    public imageDropped(files: NgxFileDropEntry[]) {
        if (this.isReadOnly) {
            return;
        }

        // Mark the image as edited.
        this.editImage = true;

        // Ignore folders.
        const uploadedImage = files[0];
        if (!uploadedImage.fileEntry.isFile) {
            return;
        }

        // Validate and preview the file.
        const fileEntry = uploadedImage.fileEntry as FileSystemFileEntry;
        fileEntry.file((file) => {
            if (!UploadUtils.isFileAllowed(file)) {
                alert("File is not a supported image");
                return;
            }
            if (!UploadUtils.isFileSizeAllowed(file)) {
                alert("File Size is larger than 2 MB");
                return;
            }

            this.uploadedFile = file;
            this.imageLoading = true;
            this.cdr.detectChanges();

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                this.imageUrl = reader.result;
                this.imageLoading = false;
                this.cdr.detectChanges();
            };
        });
    }

    // Get the Cloudinary preview image.
    get imageCloudinaryImage(): CloudinaryImage | null {
        return this.getCloudinaryImage(this.imageUrl, 800, 600, "limit");
    }

    // Build a reusable Cloudinary image instance.
    public getCloudinaryImage(
        publicId: string | null | undefined,
        width: number | null = null,
        height: number | null = null,
        crop: "fill" | "limit" = "limit",
    ): CloudinaryImage | null {
        const id = (publicId || "").trim();
        if (!id || id.startsWith("http://") || id.startsWith("https://") || id.startsWith("data:")) {
            return null;
        }

        const key = `${id}|${width ?? ""}|${height ?? ""}|${crop}`;
        const cached = this.cloudinaryImageCache.get(key);
        if (cached) {
            return cached;
        }

        const image = this.cloudinary.image(id).format("auto").quality("auto");

        if (width !== null || height !== null) {
            const resizeAction = crop === "fill" ? fill() : limitFit();
            if (width !== null) {
                resizeAction.width(width);
            }
            if (height !== null) {
                resizeAction.height(height);
            }
            image.resize(resizeAction);
        }

        this.cloudinaryImageCache.set(key, image);
        return image;
    }

    // Check whether the selected device is an NTI XL3.
    // NTI devices (e.g. XL3) use XL3-specific connection fields.
    get isNTIDevice(): boolean {
        const baseDevice = this.baseDevice;
        if (!baseDevice) {
            return false;
        }
        const manufacturerName = baseDevice.manufacturer ? this.manufacturerNameById[baseDevice.manufacturer] : undefined;
        return manufacturerName === this.ntiManufacturerName || baseDevice.model === "XL3";
    }

    // Only Bruel & Kjær devices use the FTP Folder field (as the device host).
    get showsFtpFolder(): boolean {
        const manufacturerId = this.baseDevice?.manufacturer;
        return !!manufacturerId && this.manufacturerNameById[manufacturerId] === this.bruelManufacturerName;
    }

    // Check whether the selected device is a Svantek device (by manufacturer).
    get isSvantekDevice(): boolean {
        const manufacturerId = this.baseDevice?.manufacturer;
        const manufacturerName = manufacturerId ? this.manufacturerNameById[manufacturerId] : undefined;
        if (manufacturerName && manufacturerName.toLowerCase() === this.svantekManufacturerName.toLowerCase()) {
            return true;
        }
        return this.baseDevice?.protocol?.name?.toLowerCase() === this.svantekProtocolName;
    }

    // Check whether the selected device is a CESVA LF010.
    get isLF010Device(): boolean {
        const baseDevice = this.baseDevice;
        return baseDevice?.model?.toUpperCase() === "LF010";
    }

    get lf010PeriodMappingsFormArray(): UntypedFormArray {
        return this.deviceForm.get("settings.lf010.periodMappings") as UntypedFormArray;
    }

    get lf010DevicePeriods(): LF010DevicePeriod[] {
        return this.device?.settings?.lf010?.devicePeriods?.length
            ? this.device.settings.lf010.devicePeriods
            : this.defaultLF010DevicePeriods;
    }

    get projectPeriods(): ProjectPeriodModel[] {
        return this.project?.periods ?? [];
    }

    get canConfigureLF010Periods(): boolean {
        return this.isLF010Device;
    }

    get isReadOnly(): boolean {
        return this.action === ProjectAction.EDIT && !!this.device?.active;
    }

    get availableNotificationChannels(): NotificationChannelModel[] {
        return this.project?.notificationChannels ?? [];
    }

    get hasLF010EmissionLimits(): boolean {
        return this.lf010DevicePeriods.every((devicePeriod) => typeof devicePeriod.emissionLimit === "number");
    }

    get defaultSvantekConfig(): string {
        return this.defaultSvantekConfigValue;
    }

    get svantekConfigBuilderForm(): UntypedFormGroup {
        return this.deviceForm.get("svantekConfigBuilder") as UntypedFormGroup;
    }

    get usesSvantekWaveRecording(): boolean {
        return this.svantekConfigBuilderForm?.get("waveRecordingMode")?.value !== "0";
    }

    get showsSvantekWaveTriggerPeriod(): boolean {
        const mode = this.svantekConfigBuilderForm?.get("waveRecordingMode")?.value;
        return ["2", "3", "4", "5", "6"].includes(mode);
    }

    get showsSvantekWavePreTrigger(): boolean {
        const mode = this.svantekConfigBuilderForm?.get("waveRecordingMode")?.value;
        return ["2", "3", "4", "5", "6"].includes(mode);
    }

    get showsSvantekWaveTriggerLevel(): boolean {
        const mode = this.svantekConfigBuilderForm?.get("waveRecordingMode")?.value;
        return mode === "4" || mode === "5";
    }

    get showsSvantekWaveTriggerGradient(): boolean {
        const mode = this.svantekConfigBuilderForm?.get("waveRecordingMode")?.value;
        return ["2", "3", "6"].includes(mode);
    }

    get usesSvantekLengthLimitMinutes(): boolean {
        return this.svantekConfigBuilderForm?.get("lengthLimitMode")?.value === "minutes";
    }

    get isSvantekCustomConfigValid(): boolean {
        return this.svantekConfigState?.isValid ?? true;
    }

    get showsSvantekCompatibilityWarning(): boolean {
        return this.svantekConfigState?.hasPinnedDifferences ?? false;
    }

    get svantekRawConfigDiffSegments(): SvantekDiffTokenSegment[] {
        return buildSvantekDiffTokenSegments(this.deviceForm?.get("settings.runtime.customConfig")?.value);
    }

    get svantekMissingDefaultTokens(): string[] {
        return buildMissingSvantekDefaultTokens(this.deviceForm?.get("settings.runtime.customConfig")?.value);
    }

    // Check whether the restart schedule is enabled in the form.
    get isScheduledRestartEnabled(): boolean {
        return !!this.deviceForm?.get("settings.scheduling.restart.enabled")?.value;
    }

    // Check whether the weekly schedule fields should be displayed.
    get usesWeeklyScheduledRestart(): boolean {
        return this.deviceForm?.get("settings.scheduling.restart.frequency")?.value === ScheduledRestartFrequency.Weekly;
    }

    // Expose the project timezone used for the schedule.
    get scheduledRestartTimezone(): string {
        return this.project?.timezone || this.device?.settings?.scheduling?.restart?.timezone || "Europe/Madrid";
    }

    // Check whether the NTI device uses a connect key.
    get usesConnectKey(): boolean {
        return this.deviceForm?.get("xl3ConnectionType")?.value === XL3ConnectionMode.Connect;
    }

    // Disable location actions when the form section is locked.
    get locationDisabled(): boolean {
        return this.deviceForm?.get("location")?.disabled ?? true;
    }

    // Save the Leaflet map instance.
    onMapReady(map: LeafletMap): void {
        // Cache the map and sync the initial marker.
        this.map = map;
        this.updateMapLocation();

        // Fix layout after the drawer/dialog animation.
        setTimeout(() => this.refreshMapPosition(), 0);
        setTimeout(() => this.refreshMapPosition(), 150);
    }

    // Select a location directly from the map.
    onMapClick(event: LeafletMouseEvent): void {
        // Ignore clicks when the location is locked.
        if (this.locationDisabled) {
            return;
        }

        // Update the coordinates from the clicked point.
        this.setLocationCoordinates(event.latlng.lat, event.latlng.lng);

        // Try to resolve a readable address for the marker.
        this.reverseLookupLoading = true;
        this.geocodingService
            .reverseGeocode(event.latlng.lat, event.latlng.lng)
            .pipe(finalize(() => (this.reverseLookupLoading = false)))
            .subscribe({
                next: (address) => {
                    if (!address) {
                        return;
                    }

                    this.deviceForm.get("location.address").setValue(address);
                    this.lastResolvedAddress = address.trim();
                },
                error: () => {
                    this.toastr.warning(this.translation.translateService.instant("DEVICES.ReverseGeocodeFailed"));
                },
            });
    }

    // Resolve the typed address into coordinates.
    searchAddressOnBlur(): void {
        // Stop when the address is empty, unchanged or disabled.
        if (!this.shouldSearchAddress()) {
            return;
        }

        // Read the current address text.
        const address = this.getCurrentAddress();
        this.addressLookupLoading = true;

        // Request the coordinates from the provider.
        this.geocodingService
            .geocodeAddress(address)
            .pipe(finalize(() => (this.addressLookupLoading = false)))
            .subscribe({
                next: (location) => {
                    if (!location) {
                        this.toastr.warning(this.translation.translateService.instant("DEVICES.AddressNotFound"));
                        return;
                    }

                    this.setLocationCoordinates(location.latitude, location.longitude);
                    this.deviceForm.get("location.address").setValue(location.address);
                    this.lastResolvedAddress = location.address.trim();
                },
                error: () => {
                    this.toastr.error(this.translation.translateService.instant("DEVICES.GeocodeFailed"));
                },
            });
    }

    // Check whether the current address should be geocoded.
    private shouldSearchAddress(): boolean {
        // Skip when the location cannot be edited.
        if (this.locationDisabled || this.addressLookupLoading) {
            return false;
        }

        // Skip when the address is empty or already resolved.
        const address = this.getCurrentAddress();
        return !!address && address !== this.lastResolvedAddress;
    }

    // Read the trimmed address from the form.
    private getCurrentAddress(): string {
        return this.deviceForm?.get("location.address")?.value?.trim() ?? "";
    }

    // Update validators for the scheduled restart fields.
    private updateScheduledRestartValidators() {
        // Get the schedule controls.
        const enabledControl = this.deviceForm.get("settings.scheduling.restart.enabled");
        const frequencyControl = this.deviceForm.get("settings.scheduling.restart.frequency");
        const weekdayControl = this.deviceForm.get("settings.scheduling.restart.weekday");
        const monthDayControl = this.deviceForm.get("settings.scheduling.restart.monthDay");
        const hourControl = this.deviceForm.get("settings.scheduling.restart.hour");
        const minuteControl = this.deviceForm.get("settings.scheduling.restart.minute");

        // Clear validators for hidden or disabled fields.
        weekdayControl.clearValidators();
        monthDayControl.clearValidators();
        hourControl.clearValidators();
        minuteControl.clearValidators();
        frequencyControl.clearValidators();

        // Skip when the section is not applicable.
        if (!this.isSvantekDevice || !enabledControl.value) {
            frequencyControl.updateValueAndValidity({ emitEvent: false });
            weekdayControl.updateValueAndValidity({ emitEvent: false });
            monthDayControl.updateValueAndValidity({ emitEvent: false });
            hourControl.updateValueAndValidity({ emitEvent: false });
            minuteControl.updateValueAndValidity({ emitEvent: false });
            return;
        }

        // Apply validators for the active schedule fields.
        frequencyControl.setValidators([Validators.required]);
        hourControl.setValidators([Validators.required, Validators.min(0), Validators.max(23)]);
        minuteControl.setValidators([Validators.required, Validators.min(0), Validators.max(59)]);
        if (frequencyControl.value === ScheduledRestartFrequency.Monthly) {
            monthDayControl.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);
        } else {
            weekdayControl.setValidators([Validators.required]);
        }

        // Refresh the form validity.
        frequencyControl.updateValueAndValidity({ emitEvent: false });
        weekdayControl.updateValueAndValidity({ emitEvent: false });
        monthDayControl.updateValueAndValidity({ emitEvent: false });
        hourControl.updateValueAndValidity({ emitEvent: false });
        minuteControl.updateValueAndValidity({ emitEvent: false });
    }

    // Refresh the marker and map viewport from the form values.
    private updateMapLocation() {
        // Read the current coordinates from the form.
        const coordinates = this.getFormCoordinates();

        // Reset the map when coordinates are missing.
        if (!coordinates) {
            this.mapLayers = [];
            this.mapCenter = this.defaultMapCenter;
            this.mapZoom = this.defaultMapZoom;
            this.refreshMapPosition();
            return;
        }

        // Rebuild the marker at the selected location.
        this.mapCenter = latLng(coordinates[0], coordinates[1]);
        this.mapZoom = this.selectedMapZoom;
        this.mapLayers = [
            marker([coordinates[0], coordinates[1]], {
                icon: this.locationMarkerIcon,
            }),
        ];
        this.refreshMapPosition();
    }

    // Apply coordinates to the location form.
    private setLocationCoordinates(latitude: number, longitude: number) {
        // Write the numeric coordinates with a stable precision.
        this.deviceForm.get("location").patchValue({
            latitude: Number(latitude.toFixed(6)),
            longitude: Number(longitude.toFixed(6)),
        });
    }

    // Read valid coordinates from the form.
    private getFormCoordinates(): [number, number] | null {
        // Parse the form values.
        const latitude = Number(this.deviceForm?.get("location.latitude")?.value);
        const longitude = Number(this.deviceForm?.get("location.longitude")?.value);

        // Reject invalid coordinates.
        if (Number.isNaN(latitude) || Number.isNaN(longitude) || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
            return null;
        }

        return [latitude, longitude];
    }

    // Sync the Leaflet view with the selected coordinates.
    private refreshMapPosition() {
        // Skip until the map is ready.
        if (!this.map) {
            return;
        }

        // Resize the map after dialog layout changes.
        this.map.invalidateSize({ pan: false });

        // Center the map on the selected point or default view.
        this.map.setView(this.mapCenter, this.mapZoom, { animate: false });
    }

    // Update the XL3 validators for the selected connection type.
    private updateXL3Validators() {
        // Get the relevant controls.
        const ftpFolderControl = this.deviceForm.get("ftpFolder");
        const hostControl = this.deviceForm.get("settings.connection.host");
        const passwordControl = this.deviceForm.get("settings.connection.password");
        const connectKeyControl = this.deviceForm.get("settings.connection.connectKey");

        // Reset the validators before recalculating them.
        ftpFolderControl.clearValidators();
        hostControl.clearValidators();
        passwordControl.clearValidators();
        connectKeyControl.clearValidators();

        // Require the fields that apply to NTI devices.
        if (this.isNTIDevice) {
            passwordControl.setValidators([Validators.required]);
            if (this.usesConnectKey) {
                connectKeyControl.setValidators([Validators.required]);
            } else {
                hostControl.setValidators([Validators.required]);
            }
        }

        // Recalculate the control validity without loops.
        ftpFolderControl.updateValueAndValidity({ emitEvent: false });
        hostControl.updateValueAndValidity({ emitEvent: false });
        passwordControl.updateValueAndValidity({ emitEvent: false });
        connectKeyControl.updateValueAndValidity({ emitEvent: false });
    }

    // Keep storage validation and defaulting aligned with the selected device type.
    private updateStorageControl() {
        const storageControl = this.deviceForm.get("storage");
        if (!storageControl) {
            return;
        }

        storageControl.clearValidators();

        if (this.isNTIDevice) {
            storageControl.setValidators([Validators.required, Validators.min(1)]);
            if (this.action === ProjectAction.NEW && !(Number(storageControl.value) > 0)) {
                storageControl.setValue(this.defaultXL3StorageMB, { emitEvent: false });
            }
            storageControl.updateValueAndValidity({ emitEvent: false });
            return;
        }

        if (this.action === ProjectAction.NEW && storageControl.value !== null) {
            storageControl.setValue(null, { emitEvent: false });
        }
        storageControl.updateValueAndValidity({ emitEvent: false });
    }

    // Keep the raw Svantek config and the visual builder synchronized.
    private bindSvantekConfigChanges() {
        const rawConfigControl = this.deviceForm.get("settings.runtime.customConfig");
        const builderForm = this.svantekConfigBuilderForm;

        this.applySvantekConfigState(parseSvantekCustomConfig(rawConfigControl.value));

        rawConfigControl.valueChanges.subscribe((rawValue) => {
            if (this.svantekConfigSyncing) {
                return;
            }

            this.applySvantekConfigState(parseSvantekCustomConfig(rawValue));
        });

        builderForm.valueChanges.subscribe(() => {
            if (this.svantekConfigSyncing || builderForm.disabled || !this.isSvantekCustomConfigValid) {
                return;
            }

            this.updateSvantekBuilderValidators();

            if (builderForm.invalid) {
                return;
            }

            const nextConfig = serializeSvantekCustomConfig(this.readSvantekBuilderState(), this.svantekConfigState);
            this.svantekConfigSyncing = true;
            rawConfigControl.setValue(nextConfig, { emitEvent: false });
            this.svantekConfigSyncing = false;
            this.applySvantekConfigState(parseSvantekCustomConfig(nextConfig));
        });

        this.updateSvantekBuilderValidators();
    }

    // Apply the parsed Svantek raw state to the visual builder.
    private applySvantekConfigState(parsedConfig: ParsedSvantekCustomConfig) {
        this.svantekConfigState = parsedConfig;
        this.svantekConfigSyncing = true;
        this.svantekConfigBuilderForm.patchValue(parsedConfig.supported, { emitEvent: false });
        this.svantekConfigSyncing = false;

        if (!this.shouldEnableSvantekBuilder(parsedConfig)) {
            this.svantekConfigBuilderForm.disable({ emitEvent: false });
        } else {
            this.svantekConfigBuilderForm.enable({ emitEvent: false });
        }

        this.updateSvantekBuilderValidators();
    }

    // Keep validators aligned with the currently visible builder fields.
    private updateSvantekBuilderValidators() {
        const modeControl = this.svantekConfigBuilderForm.get("waveRecordingMode");
        const waveFileNameControl = this.svantekConfigBuilderForm.get("waveFileName");
        const formatControl = this.svantekConfigBuilderForm.get("format");
        const bitsPerSampleControl = this.svantekConfigBuilderForm.get("bitsPerSample");
        const recordingTimeControl = this.svantekConfigBuilderForm.get("recordingTime");
        const preTriggerControl = this.svantekConfigBuilderForm.get("preTrigger");
        const recordingFilterControl = this.svantekConfigBuilderForm.get("recordingFilter");
        const samplingRateControl = this.svantekConfigBuilderForm.get("samplingRate");
        const gainControl = this.svantekConfigBuilderForm.get("gain");
        const triggerLevelControl = this.svantekConfigBuilderForm.get("triggerLevel");
        const triggerPeriodControl = this.svantekConfigBuilderForm.get("triggerPeriod");
        const triggerGradientControl = this.svantekConfigBuilderForm.get("triggerGradient");
        const lengthLimitModeControl = this.svantekConfigBuilderForm.get("lengthLimitMode");
        const lengthLimitMinutesControl = this.svantekConfigBuilderForm.get("lengthLimitMinutes");
        const supportedState = this.svantekConfigState?.supported ?? getDefaultSvantekVisualConfig();

        waveFileNameControl.clearValidators();
        formatControl.clearValidators();
        bitsPerSampleControl.clearValidators();
        recordingTimeControl.clearValidators();
        preTriggerControl.clearValidators();
        recordingFilterControl.clearValidators();
        samplingRateControl.clearValidators();
        gainControl.clearValidators();
        triggerLevelControl.clearValidators();
        triggerPeriodControl.clearValidators();
        triggerGradientControl.clearValidators();
        lengthLimitModeControl.clearValidators();
        lengthLimitMinutesControl.clearValidators();

        if (this.isSvantekDevice && modeControl.value !== "0") {
            waveFileNameControl.setValidators([Validators.pattern(/^[A-Z0-9_]{1,8}$/)]);
            formatControl.setValidators([Validators.required]);
            bitsPerSampleControl.setValidators([Validators.required]);
            recordingTimeControl.setValidators(this.getSvantekNumberValidators(supportedState.recordingTime, 1, 28800, true));
            recordingFilterControl.setValidators([Validators.required]);
            samplingRateControl.setValidators([Validators.required]);
            gainControl.setValidators(this.getSvantekNumberValidators(supportedState.gain, 0, 40, true));
            lengthLimitModeControl.setValidators([Validators.required]);
            if (lengthLimitModeControl.value === "minutes") {
                lengthLimitMinutesControl.setValidators(this.getSvantekNumberValidators(supportedState.lengthLimitMinutes, 1, 480, true));
            }
        }

        if (this.isSvantekDevice && this.showsSvantekWaveTriggerPeriod) {
            triggerPeriodControl.setValidators([Validators.required]);
        }

        if (this.isSvantekDevice && this.showsSvantekWavePreTrigger) {
            preTriggerControl.setValidators(this.getSvantekNumberValidators(supportedState.preTrigger, 0, 60, true));
        }

        if (this.isSvantekDevice && this.showsSvantekWaveTriggerLevel) {
            triggerLevelControl.setValidators(this.getSvantekNumberValidators(supportedState.triggerLevel, 24, 136, true));
        }

        if (this.isSvantekDevice && this.showsSvantekWaveTriggerGradient) {
            triggerGradientControl.setValidators(this.getSvantekNumberValidators(supportedState.triggerGradient, 1, 100, true));
        }

        waveFileNameControl.updateValueAndValidity({ emitEvent: false });
        formatControl.updateValueAndValidity({ emitEvent: false });
        bitsPerSampleControl.updateValueAndValidity({ emitEvent: false });
        recordingTimeControl.updateValueAndValidity({ emitEvent: false });
        preTriggerControl.updateValueAndValidity({ emitEvent: false });
        recordingFilterControl.updateValueAndValidity({ emitEvent: false });
        samplingRateControl.updateValueAndValidity({ emitEvent: false });
        gainControl.updateValueAndValidity({ emitEvent: false });
        triggerLevelControl.updateValueAndValidity({ emitEvent: false });
        triggerPeriodControl.updateValueAndValidity({ emitEvent: false });
        triggerGradientControl.updateValueAndValidity({ emitEvent: false });
        lengthLimitModeControl.updateValueAndValidity({ emitEvent: false });
        lengthLimitMinutesControl.updateValueAndValidity({ emitEvent: false });
    }

    private shouldEnableSvantekBuilder(parsedConfig: ParsedSvantekCustomConfig): boolean {
        return this.isSvantekDevice && !this.isReadOnly && parsedConfig.isValid;
    }

    private getSvantekNumberValidators(parsedRawValue: number | null, min: number, max: number, required = false): ValidatorFn[] {
        const validators: ValidatorFn[] = [
            this.allowCurrentSvantekRawValue(parsedRawValue, Validators.min(min)),
            this.allowCurrentSvantekRawValue(parsedRawValue, Validators.max(max)),
        ];

        if (required) {
            validators.unshift(Validators.required);
        }

        return validators;
    }

    private allowCurrentSvantekRawValue(parsedRawValue: number | null, validator: ValidatorFn): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (parsedRawValue !== null && parsedRawValue !== undefined && this.isSameSvantekValue(control.value, parsedRawValue)) {
                return null;
            }

            return validator(control);
        };
    }

    private isSameSvantekValue(controlValue: unknown, parsedRawValue: unknown): boolean {
        if (controlValue === parsedRawValue) {
            return true;
        }

        if (controlValue === null || controlValue === undefined || parsedRawValue === null || parsedRawValue === undefined) {
            return false;
        }

        return String(controlValue) === String(parsedRawValue);
    }

    // Read the builder values as a typed config object.
    private readSvantekBuilderState(): SvantekVisualConfigState {
        const builderValue = this.svantekConfigBuilderForm.getRawValue();

        return {
            waveFileName: (builderValue.waveFileName ?? getDefaultSvantekVisualConfig().waveFileName).trim().toUpperCase(),
            microphoneCompensation: builderValue.microphoneCompensation ?? "",
            freeFieldCompensation: builderValue.freeFieldCompensation ?? "",
            waveRecordingMode: builderValue.waveRecordingMode ?? getDefaultSvantekVisualConfig().waveRecordingMode,
            format: builderValue.format ?? getDefaultSvantekVisualConfig().format,
            bitsPerSample: builderValue.bitsPerSample ?? getDefaultSvantekVisualConfig().bitsPerSample,
            recordingTime: Number(builderValue.recordingTime ?? getDefaultSvantekVisualConfig().recordingTime),
            preTrigger: Number(builderValue.preTrigger ?? getDefaultSvantekVisualConfig().preTrigger),
            recordingFilter: builderValue.recordingFilter ?? getDefaultSvantekVisualConfig().recordingFilter,
            samplingRate: builderValue.samplingRate ?? getDefaultSvantekVisualConfig().samplingRate,
            gain: Number(builderValue.gain ?? getDefaultSvantekVisualConfig().gain),
            triggerLevel: Number(builderValue.triggerLevel ?? getDefaultSvantekVisualConfig().triggerLevel),
            triggerPeriod: builderValue.triggerPeriod ?? getDefaultSvantekVisualConfig().triggerPeriod,
            triggerGradient: Number(builderValue.triggerGradient ?? getDefaultSvantekVisualConfig().triggerGradient),
            lengthLimitMode: builderValue.lengthLimitMode ?? getDefaultSvantekVisualConfig().lengthLimitMode,
            lengthLimitMinutes:
                builderValue.lengthLimitMode === "minutes"
                    ? Number(builderValue.lengthLimitMinutes ?? getDefaultSvantekVisualConfig().lengthLimitMinutes ?? 1)
                    : null,
        };
    }

    private rebuildLF010PeriodMappings() {
        while (this.lf010PeriodMappingsFormArray.length > 0) {
            this.lf010PeriodMappingsFormArray.removeAt(0);
        }

        const mappingByDevicePeriod = new Map(
            (this.device?.settings?.lf010?.periodMappings ?? []).map((mapping) => [mapping.devicePeriod, mapping.projectPeriodName]),
        );

        this.lf010DevicePeriods.forEach((devicePeriod) => {
            this.lf010PeriodMappingsFormArray.push(
                this.formBuilder.group({
                    devicePeriod: [devicePeriod.devicePeriod],
                    projectPeriodName: [mappingByDevicePeriod.get(devicePeriod.devicePeriod) ?? null],
                }),
            );
        });
    }

    formatLF010DevicePeriodLabel(devicePeriod: LF010DevicePeriod): string {
        return typeof devicePeriod.emissionLimit === "number"
            ? `${devicePeriod.name} (${devicePeriod.emissionLimit} dBA)`
            : devicePeriod.name;
    }

    private normalizeSettings(
        settings: ProjectDeviceSettings | undefined,
        connectionMode: XL3ConnectionMode,
    ): ProjectDeviceSettings | null {
        const normalized = settings ? JSON.parse(JSON.stringify(settings)) : {};

        if (!this.isNTIDevice) {
            normalized.connection = this.isLF010Device
                ? {
                      type: "lf010",
                      host: this.normalizeOptionalString(settings?.connection?.host),
                      password: this.normalizeOptionalString(settings?.connection?.password),
                  }
                : null;
        } else {
            normalized.connection = {
                type: "xl3",
                mode: connectionMode,
                host: connectionMode === XL3ConnectionMode.Lan ? this.normalizeOptionalString(settings?.connection?.host) : null,
                password: this.normalizeOptionalString(settings?.connection?.password),
                connectKey:
                    connectionMode === XL3ConnectionMode.Connect ? this.normalizeOptionalString(settings?.connection?.connectKey) : null,
            };
        }

        normalized.lf010 = this.isLF010Device
            ? {
                  ...(this.device?.settings?.lf010 ?? {}),
                  ...(normalized.lf010 ?? {}),
                  periodMappings: normalized.lf010?.periodMappings?.filter((mapping) => mapping?.projectPeriodName) ?? [],
              }
            : null;

        if (!this.isSvantekDevice) {
            if (normalized.runtime) {
                delete normalized.runtime.customConfig;
                delete normalized.runtime.fileSyncEnabled;
            }
            normalized.scheduling = null;
        } else {
            normalized.runtime = {
                ...(normalized.runtime ?? {}),
                customConfig: this.normalizeOptionalString(settings?.runtime?.customConfig),
                fileSyncEnabled: settings?.runtime?.fileSyncEnabled === true,
            };

            const restart = settings?.scheduling?.restart;
            if (!restart?.enabled) {
                normalized.scheduling = null;
            } else {
                normalized.scheduling = {
                    restart: {
                        ...restart,
                        timezone: this.project?.timezone || this.device?.settings?.scheduling?.restart?.timezone || null,
                        weekday: restart.frequency === ScheduledRestartFrequency.Weekly ? restart.weekday : null,
                        monthDay: restart.frequency === ScheduledRestartFrequency.Monthly ? restart.monthDay : null,
                    },
                };
            }
        }

        normalized.specialAlarms = {
            connectionEnabled: !!settings?.specialAlarms?.connectionEnabled,
            powerSupplyEnabled: !!settings?.specialAlarms?.powerSupplyEnabled,
            notificationChannelIds: Array.isArray(settings?.specialAlarms?.notificationChannelIds)
                ? settings.specialAlarms.notificationChannelIds.filter((channelId) => typeof channelId === "string")
                : [],
        };
        if (
            !normalized.specialAlarms.connectionEnabled &&
            !normalized.specialAlarms.powerSupplyEnabled &&
            normalized.specialAlarms.notificationChannelIds.length === 0
        ) {
            normalized.specialAlarms = null;
        }

        if (!normalized.connection && !normalized.runtime && !normalized.scheduling && !normalized.specialAlarms) {
            return null;
        }

        return normalized;
    }

    private normalizeOptionalString(value?: string | null): string | null {
        if (typeof value !== "string") {
            return null;
        }

        const trimmed = value.trim();
        return trimmed.length ? trimmed : null;
    }
}
